<script setup lang="ts">
  import { Dialog, DialogContent } from '@/components/ui/dialog'
  import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
  import { Switch } from '@/components/ui/switch'
  import { useToast } from '@/components/ui/toast'
  import { useTranslation } from '@/composables/useTranslation'
  import { useVerdictHintPopoverGuard } from '@/composables/useVerdictHintPopoverGuard'
  import { FILE_INPUT_ACCEPT, isFileSupported } from '@/constants/documentFileTypes'
  import { useUserStore } from '@/shared/stores/userStore'
  import { hasCaseContextMessages } from '@/utils/chatCaseLock'
  import { useCaseStore } from '@/store/app/case/detail/caseDetailStore'
  import { useGlobalChatStore } from '@/store/app/globalChat/globalChatStore'
  import { useUnifiedChatStore } from '@/store/app/unifiedChat'
  import ModelSelector from '@/components/app/chat/components/ModelSelector.vue'
  import { toChatContext } from '@/utils/chatContext'
  import {
    AiChatSessionsService,
    AIChatSessionTypeEnum,
    DocumentsService,
    EventNameEnum,
    UsersService,
    type AIChatSession,
    type CustomerCase
  } from '@/types/generated'
  import { useComposerPrefill } from '@/composables/useComposerPrefill'
  import { useMetrics } from '@/composables/useMetrics'
  import {
    ArrowUp,
    FolderInput,
    Info,
    Info as InfoIcon,
    ListRestart,
    Loader2,
    Paperclip,
    Plus,
    Section
  } from 'lucide-vue-next'
  import { computed, inject, nextTick, onMounted, provide, ref, watch, type Ref } from 'vue'
  import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
  import CasePreviewDialog from '../sidepanel/CasePreviewDialog.vue'
  import CaseSearchPanel from '../sidepanel/CaseSearchPanel.vue'
  import InputBadge from '../sidepanel/InputBadge.vue'
  import AiChatVerdictFeatureHint from './AiChatVerdictFeatureHint.vue'
  import PromptTemplatesDialog from './PromptTemplatesDialog.vue'

  const { t } = useTranslation({
    en: {
      YourMessage: 'Your message...',
      FullscreenPlaceholder: 'Start a task or ask a question...',
      Generating: 'Generating...',
      SendMessage: 'Send message',
      PromptTemplates: 'Prompt templates',
      Prompts: 'Prompts',
      ModelLabel: 'GPT 5.4',
      AddDocuments: 'Add documents',
      AddRequest: 'Add request',
      PlusMenuAria: 'Add documents',
      EnhancePrompt: 'Enhance Prompt',
      Enhancing: 'Enhancing',
      CourtDatabase: 'Court database',
      caseAlreadyAdded:
        'This request has already been added. Please start a new chat for additional requests.',
      more: 'more',
      unsupportedFiles: 'file(s) not supported',
      uploadFailed: 'Upload failed for',
      createSessionFailed: 'Failed to create session',
      attachCaseFailed: 'Failed to attach case',
      attachCaseNewChatRequired: 'Please start a new chat to attach a case to this conversation.',
      enhancePromptFailed: 'Failed to enhance prompt. Please try again.'
    },
    de: {
      YourMessage: 'Ihre Nachricht...',
      FullscreenPlaceholder: 'Aufgabe starten oder fragen...',
      Generating: 'Generiere...',
      SendMessage: 'Nachricht senden',
      PromptTemplates: 'Promptvorlagen',
      Prompts: 'Prompts',
      ModelLabel: 'GPT 5.4',
      AddDocuments: 'Dokumente hinzufügen',
      AddRequest: 'Anfrage hinzufügen',
      PlusMenuAria: 'Dokumente hinzufügen',
      EnhancePrompt: 'Prompt verbessern',
      Enhancing: 'Verbessert',
      CourtDatabase: 'Urteilsdatenbank',
      caseAlreadyAdded:
        'Diese Anfrage wurde bereits hinzugefügt. Für weitere Anfragen bitte neuen Chat starten.',
      more: 'weitere',
      unsupportedFiles: 'Datei(en) nicht unterstützt',
      uploadFailed: 'Upload fehlgeschlagen für',
      createSessionFailed: 'Sitzung konnte nicht erstellt werden',
      attachCaseFailed: 'Anfrage konnte nicht hinzugefügt werden',
      attachCaseNewChatRequired:
        'Bitte starten Sie einen neuen Chat, um eine Anfrage an diese Konversation anzuhängen.',
      enhancePromptFailed: 'Prompt-Verbesserung fehlgeschlagen. Bitte erneut versuchen.'
    },
    fr: {
      YourMessage: 'Votre message...',
      FullscreenPlaceholder: 'Démarrer une tâche ou poser une question...',
      Generating: 'Génération...',
      SendMessage: 'Envoyer le message',
      PromptTemplates: 'Modèles de prompts',
      Prompts: 'Modèles',
      ModelLabel: 'GPT 5.4',
      AddDocuments: 'Ajouter des documents',
      AddRequest: 'Ajouter une demande',
      PlusMenuAria: 'Ajouter des documents',
      EnhancePrompt: 'Améliorer le prompt',
      Enhancing: 'Amélioration',
      CourtDatabase: 'Base de données judiciaire',
      caseAlreadyAdded:
        'Cette demande a déjà été ajoutée. Veuillez démarrer une nouvelle conversation pour des demandes supplémentaires.',
      more: 'autres',
      unsupportedFiles: 'fichier(s) non pris en charge',
      uploadFailed: 'Échec du téléversement pour',
      createSessionFailed: 'Échec de la création de la session',
      attachCaseFailed: "Échec de l'ajout de la demande",
      attachCaseNewChatRequired:
        'Veuillez démarrer une nouvelle conversation pour attacher une demande.',
      enhancePromptFailed: "Échec de l'amélioration du prompt. Veuillez réessayer."
    }
  })

  // Inject case data and chat functionality
  const caseData = inject<Ref<CustomerCase | null>>('caseData', ref(null))
  const sessionType = inject<AIChatSessionTypeEnum>('sessionType', AIChatSessionTypeEnum.CASE)
  const centerAiAssistantComposer = inject(
    'centerAiAssistantComposer',
    computed(() => false)
  )

  /** Vue 3.4 doesn't ship `useId` — keep this local so SVG ids stay unique across mounts. */
  const generateGradientId = () => {
    const raw =
      typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`
    return raw.replace(/[^a-zA-Z0-9_-]/g, '')
  }
  const sparkleGradientId = `ai-enhance-grad-${generateGradientId()}`
  const spinnerGradientId = `ai-enhance-spinner-${generateGradientId()}`

  /** Long Tailwind arbitrary classes break Vue's in-template :class expression parser (Vite 500). */
  const enhancePromptVariantClass =
    'h-8 w-8 overflow-hidden rounded-[60px] p-1.5 outline outline-1 -outline-offset-1 outline-pink-500 transition-opacity disabled:cursor-default'

  const sendButtonVariantClass =
    'h-8 w-8 overflow-hidden rounded-[60px] bg-primary p-1.5 text-primary-foreground shadow-[0px_2px_4px_0px_rgba(0,0,0,0.04)] hover:bg-primary/90 focus:bg-primary/90'

  const sessionGroupData = inject<Ref<any>>('sessionGroupData', ref(null))

  const messagePlaceholder = computed(() => t('FullscreenPlaceholder'))
  const sendChatMessage = inject(
    'sendChatMessage',
    async (_session: AIChatSession | null, _message: string) => {}
  )
  /**
   * Create a new chat session on demand; injected from :class:`AiChat` so that a session can be
   * materialised before uploads / case-attach without relying on :func:`sendChatMessage` (which
   * would otherwise push the user message first and race the partial update).
   *
   * :param sessionGroupUuid: Optional folder/session group for global chats.
   * :param caseUuid: When provided on a global context, forces a ``UNIFIED`` session
   *     with the case attached at creation (``GLOBAL`` sessions reject ``case_uuid``).
   */
  const createNewChatSession = inject<
    (sessionGroupUuid?: string, caseUuid?: string) => Promise<AIChatSession | null>
  >('createNewChatSession', async () => null)

  // Thread context annotations (frontend-only) — see :class:`AiChat` provide.
  type ThreadContextAnnotationData = {
    caseLabel?: string
    caseUuid?: string
    documents?: Array<{ filename: string; uuid: string }>
  }
  const pushThreadContextAnnotation = inject<
    (sessionUuid: string, data: ThreadContextAnnotationData) => void
  >('pushThreadContextAnnotation', () => {})
  const clearThreadContextAnnotationsForSession = inject<(sessionUuid: string) => void>(
    'clearThreadContextAnnotationsForSession',
    () => {}
  )
  const isSessionGenerating = inject<(uuid: string) => boolean>('isSessionGenerating', () => false)
  const activeChatSession = inject<Ref<AIChatSession | null>>('activeChatSession', ref(null))
  const isActiveSessionGenerating = computed(() => {
    return activeChatSession.value && isSessionGenerating(activeChatSession.value.uuid)
  })
  const userChatMessage = ref('')
  const textareaRef = ref<HTMLTextAreaElement | null>(null)
  const isTextareaScrollable = ref(false)
  const courtDatabaseToggleRef = ref<HTMLElement | null>(null)
  const fileInputRef = ref<HTMLInputElement | null>(null)

  // Get stores for document processing data
  const caseStore = useCaseStore()
  const globalChatStore = useGlobalChatStore()
  const userStore = useUserStore()
  const { toast } = useToast()
  const unifiedChatStore = useUnifiedChatStore()

  const selectedModelId = inject<Ref<string | null>>('selectedModelId', ref(null))

  // ---------------
  // Document upload state (badges shown above textarea)
  // ---------------

  interface PendingInputDoc {
    id: string
    filename: string
    variant: 'pdf' | 'word' | 'other'
    loading: boolean
    uuid?: string
  }

  /** Pending document badges displayed above the textarea while uploads are in-flight. */
  const pendingInputDocs = ref<PendingInputDoc[]>([])

  const uploadsInProgress = computed(() => pendingInputDocs.value.some((d) => d.loading))

  /**
   * UUIDs of documents uploaded but not yet sent in a message. Cleaned up (deleted from the
   * backend) if the user starts a new chat, switches sessions, or removes the badge before
   * sending — mirrors the side-chat panel contract.
   */
  const uncommittedDocUuids = ref<string[]>([])

  /** Derive a file-type badge variant from a filename extension. */
  function fileVariant(filename: string): 'pdf' | 'word' | 'other' {
    const ext = filename.split('.').pop()?.toLowerCase() ?? ''
    if (ext === 'pdf') return 'pdf'
    if (['doc', 'docx'].includes(ext)) return 'word'
    return 'other'
  }

  /**
   * Delete all uncommitted documents from the backend. Fire-and-forget — a failure
   * here leaves an orphan document row (harmless for the user, but we still log so
   * the incident surfaces in observability instead of disappearing silently).
   */
  function discardUncommittedDocs() {
    const uuids = uncommittedDocUuids.value.splice(0)
    for (const uuid of uuids) {
      DocumentsService.documentsDestroy({ uuid }).catch((error: unknown) => {
        console.error('Failed to discard uncommitted document:', uuid, error)
      })
    }
  }

  // ---------------
  // Case-attach state (mirrors SidePanelChat behaviour)
  // ---------------

  const manualCase = ref<{ uuid: string; label: string } | null>(null)
  /**
   * Optimistic lock flag flipped on the first local send after attaching a case. Covers the
   * window between :func:`sendMessage` and the backend round-trip that refreshes
   * ``session.messages``. Do **not** read this directly for UI lock purposes — use
   * :const:`caseLocked`, which also consults the authoritative session payload so the lock
   * survives reloads, session switches, and side-panel ↔ fullscreen transitions without
   * depending on component-local state.
   */
  const caseSentWithContext = ref(false)

  /**
   * Whether the attached case is pinned to the session (remove affordance must be hidden).
   * Mirrors the backend detach rule in :func:`assert_case_detach_allowed`: any persisted
   * user or assistant message with ``created_at >= case_linked_at`` locks the case. Falls
   * back to the local ``caseSentWithContext`` flag only for the narrow window between a
   * first send and the API refresh that repopulates ``session.messages``.
   */
  const caseLocked = computed(() => {
    const session = activeChatSession.value
    if (!session?.case_linked_at) return false
    if (caseSentWithContext.value) return true
    return hasCaseContextMessages(session)
  })

  const activeCaseLabel = computed(() => manualCase.value?.label ?? null)
  const hasActiveCase = computed(() => !!manualCase.value)

  // ---------------
  // Chat functionality
  // ---------------

  /**
   * In-flight session creation promise. Callers that race (e.g. drop-upload and
   * send-click in the same tick) share the same creation attempt instead of
   * firing two ``aiChatSessionsCreate`` requests and leaving the losing set of
   * pending-doc badges attached to an orphan session.
   */
  let sessionCreationPromise: Promise<AIChatSession | null> | null = null

  /**
   * Ensure a chat session exists so document uploads and case attachment have a target.
   *
   * When a case is pending on the input (``manualCase``) and no session exists yet, this
   * creates a ``UNIFIED`` session with the case linked at creation — the backend rejects
   * attaching a case to a ``GLOBAL`` session via PATCH. When no case is pending, a plain
   * ``GLOBAL`` session is created.
   *
   * Concurrent invocations (e.g. upload-drop + send button firing in the same tick) share
   * the same in-flight promise so only one session is created.
   *
   * :return: The resolved :class:`AIChatSession` or ``null`` when creation failed.
   */
  async function ensureSession(): Promise<AIChatSession | null> {
    if (activeChatSession.value) return activeChatSession.value
    if (sessionCreationPromise) return sessionCreationPromise
    sessionCreationPromise = (async () => {
      try {
        const sessionGroupUuid = sessionGroupData.value?.uuid || undefined
        const caseUuid = manualCase.value?.uuid
        const session = await createNewChatSession(sessionGroupUuid, caseUuid)
        return session ?? activeChatSession.value
      } catch (error) {
        console.error('Failed to create chat session:', error)
        toast({ title: t('createSessionFailed'), variant: 'destructive' })
        return null
      } finally {
        sessionCreationPromise = null
      }
    })()
    return sessionCreationPromise
  }

  const sendMessage = async () => {
    if (!userChatMessage.value.trim() || isActiveSessionGenerating.value) return
    if (uploadsInProgress.value) return

    const trimmed = userChatMessage.value.trim()

    // Docs in pending are now considered committed — don't delete on close/new-chat
    uncommittedDocUuids.value = []

    // Ensure a session exists before sending. If a case is pending, this creates the
    // session as ``UNIFIED`` with the case attached (and sets ``activeChatSession``).
    const needsSession =
      !activeChatSession.value && (hasActiveCase.value || pendingInputDocs.value.length > 0)
    if (needsSession) {
      const session = await ensureSession()
      if (!session) return
    } else if (
      activeChatSession.value &&
      hasActiveCase.value &&
      !caseSentWithContext.value &&
      activeChatSession.value.case_uuid !== manualCase.value?.uuid
    ) {
      // Existing session whose persisted ``case_uuid`` doesn't match the staged case —
      // either no case has been linked yet (initial attach) or the user switched the
      // staged case pre-send (e.g. after ``ensureSession`` linked the original case
      // during a doc upload). Either way, PATCH the new ``case_uuid`` before sending so
      // the backend uses the right case for context.
      const ok = await commitCaseToExistingSession()
      if (!ok) {
        toast({ title: t('attachCaseFailed'), variant: 'destructive' })
        return
      }
    }

    // Snapshot pending-attachment data for the thread annotation *before* we clear the
    // input-level state. The annotation marks the user message about to be sent.
    const annotationSession = activeChatSession.value
    const annotationDocs = pendingInputDocs.value
      .filter((d) => !!d.uuid)
      .map((d) => ({ filename: d.filename, uuid: d.uuid as string }))
    const annotationCase =
      hasActiveCase.value && !caseSentWithContext.value ? manualCase.value : null

    if (annotationSession?.uuid && (annotationDocs.length > 0 || !!annotationCase)) {
      pushThreadContextAnnotation(annotationSession.uuid, {
        caseLabel: annotationCase?.label,
        caseUuid: annotationCase?.uuid,
        documents: annotationDocs
      })
    }

    if (hasActiveCase.value && !caseSentWithContext.value) {
      caseSentWithContext.value = true
    }

    // Clear doc badges from the input — uploads are already attached to the session
    pendingInputDocs.value = []

    userChatMessage.value = ''
    nextTick(() => {
      adjustTextareaHeight()
    })

    await sendChatMessage(activeChatSession.value, trimmed)
  }

  // ---------------
  // Prompt Enhancement functionality
  // ---------------

  const isEnhancingPrompt = ref(false)
  const enhancementError = ref<string | null>(null)
  const showEnhanceTooltip = ref(false)

  const enhancePrompt = async () => {
    if (
      !userChatMessage.value.trim() ||
      isEnhancingPrompt.value ||
      isActiveSessionGenerating.value
    ) {
      return
    }

    isEnhancingPrompt.value = true
    enhancementError.value = null

    try {
      const response = await AiChatSessionsService.aiChatSessionsEnhancePromptCreate({
        requestBody: {
          content: userChatMessage.value.trim()
        }
      })

      // Replace the user's input with the enhanced prompt
      userChatMessage.value = response.enhanced_content
      // Adjust textarea height for the new content
      nextTick(() => {
        adjustTextareaHeight()
      })
    } catch (error) {
      console.error('Failed to enhance prompt:', error)
      enhancementError.value = t('enhancePromptFailed')

      // Auto-hide error message after 5 seconds
      setTimeout(() => {
        enhancementError.value = null
      }, 5000)
    } finally {
      isEnhancingPrompt.value = false
    }
  }

  const isPromptPopoverOpen = inject<Ref<boolean>>('isPromptPopoverOpen', ref(false))

  const openPromptTemplates = () => {
    showPlusPopover.value = false
    isPromptPopoverOpen.value = true
  }

  const showPlusPopover = ref(false)
  /** Inner popover panel — mirrors SidePanelChatInput: main menu or case search. */
  const plusPanel = ref<'main' | 'cases'>('main')

  // Case preview dialog state (for the Eye icon in the case search list)
  const previewOpen = ref(false)
  const previewCaseUuid = ref<string | null>(null)
  const previewCaseLabel = ref('')

  const showVerdictFeatureHint = ref(false)

  watch(showPlusPopover, (open) => {
    if (!open) {
      plusPanel.value = 'main'
    }
  })

  // Get the appropriate document processor
  const documentProcessor = computed(() => {
    if (sessionType === AIChatSessionTypeEnum.CASE && caseData.value?.uuid) {
      return caseStore.documentProcessor
    } else if (sessionType === AIChatSessionTypeEnum.GLOBAL) {
      if (activeChatSession.value?.uuid) {
        return globalChatStore.getSessionProcessor(activeChatSession.value.uuid)
      }
      return null
    }
    return null
  })

  // ---------------
  // + menu → Add documents (direct file picker)
  // ---------------

  function triggerFilePicker() {
    showPlusPopover.value = false
    fileInputRef.value?.click()
  }

  async function onUploadDocuments(files: File[]) {
    const supported = files.filter(isFileSupported)
    const unsupported = files.length - supported.length

    if (unsupported > 0) {
      toast({
        title: `${unsupported} ${t('unsupportedFiles')}`,
        variant: 'destructive'
      })
    }
    if (supported.length === 0) return

    const session = await ensureSession()
    if (!session) return

    const entries: PendingInputDoc[] = supported.map((file) => ({
      id: crypto.randomUUID(),
      filename: file.name,
      variant: fileVariant(file.name),
      loading: true
    }))
    pendingInputDocs.value.push(...entries)

    const processor = documentProcessor.value
    if (!processor) {
      // Should not happen after ensureSession, but guard just in case
      for (const entry of entries) {
        const idx = pendingInputDocs.value.findIndex((d) => d.id === entry.id)
        if (idx >= 0) pendingInputDocs.value.splice(idx, 1)
      }
      return
    }

    for (let i = 0; i < supported.length; i++) {
      try {
        const doc = await processor.uploadDocument(supported[i])
        const idx = pendingInputDocs.value.findIndex((d) => d.id === entries[i].id)
        if (doc?.uuid) {
          uncommittedDocUuids.value.push(doc.uuid)
          if (idx >= 0) {
            pendingInputDocs.value[idx] = {
              ...pendingInputDocs.value[idx],
              loading: false,
              uuid: doc.uuid
            }
          }
        } else {
          if (idx >= 0) pendingInputDocs.value.splice(idx, 1)
          toast({
            title: `${t('uploadFailed')} ${supported[i].name}`,
            variant: 'destructive'
          })
        }
      } catch (error) {
        console.error('Failed to upload document:', error)
        const idx = pendingInputDocs.value.findIndex((d) => d.id === entries[i].id)
        if (idx >= 0) pendingInputDocs.value.splice(idx, 1)
      }
    }
  }

  function onFileInput(event: Event) {
    const input = event.target as HTMLInputElement
    const files = Array.from(input.files ?? [])
    if (files.length > 0) {
      void onUploadDocuments(files)
    }
    input.value = ''
  }

  // ---------------
  // + menu → Add request (case search & attach)
  // ---------------

  /**
   * Stage a case on the composer.
   *
   * Pre-send the case is purely local (``manualCase``) and can be replaced freely so the
   * user can switch contexts without removing the badge first. Persistence to the session
   * happens at first send via :func:`commitCaseToExistingSession`, which PATCHes the
   * staged ``case_uuid`` only when it differs from the session's currently-linked case.
   *
   * The only blocking guards are: legacy ``GLOBAL`` sessions (the backend rejects
   * ``case_uuid`` PATCH on those rows) and the post-send lock — once a message has been
   * sent in case context, the popover renders the disabled/info-icon state and the user
   * never reaches this handler.
   */
  async function onSelectCase(uuid: string, label: string) {
    const session = activeChatSession.value
    if (session) {
      if (
        session.session_type !== AIChatSessionTypeEnum.UNIFIED &&
        session.session_type !== AIChatSessionTypeEnum.CASE
      ) {
        toast({ title: t('attachCaseNewChatRequired'), variant: 'destructive' })
        showPlusPopover.value = false
        return
      }
      if (caseLocked.value) {
        toast({ title: t('caseAlreadyAdded'), variant: 'destructive' })
        showPlusPopover.value = false
        return
      }
    }
    manualCase.value = { uuid, label }
    showPlusPopover.value = false
  }

  /**
   * Persist the staged case onto an existing session via PATCH.
   *
   * Used when the user attached a case to a session that already exists (typically a
   * ``UNIFIED``-no-case session) and also when the user switched the staged case pre-send
   * on a session that was created with a different case attached (e.g. ``ensureSession``
   * fired during a doc upload). For brand-new sessions, ``ensureSession`` already passes
   * ``case_uuid`` at creation so no follow-up PATCH is needed.
   *
   * The early-return is keyed on case identity (not on whether *any* case is linked) so a
   * pre-send switch from case A to case B reaches the PATCH path. The post-send lock is
   * enforced upstream by :const:`caseLocked` — by the time this runs, any switch has
   * already been blocked at the popover.
   *
   * :return: ``true`` when the link was persisted (or no work was required), ``false`` on
   *     a hard failure (caller can decide whether to surface a toast).
   */
  async function commitCaseToExistingSession(): Promise<boolean> {
    const session = activeChatSession.value
    const mc = manualCase.value
    if (!session?.uuid || !mc?.uuid) return true
    if (session.case_uuid === mc.uuid) return true
    try {
      const updated = await AiChatSessionsService.aiChatSessionsPartialUpdate({
        uuid: session.uuid,
        requestBody: { case_uuid: mc.uuid }
      })
      // Preserve any in-flight / streaming messages — the PATCH response is from a separate
      // request and may not include the latest message buffer.
      const merged: AIChatSession = {
        ...updated,
        messages:
          (session.messages?.length ?? 0) > (updated.messages?.length ?? 0)
            ? session.messages
            : updated.messages
      }
      globalChatStore.setActiveChatSession(merged)
      return true
    } catch (error) {
      console.error('Failed to attach case to session:', error)
      return false
    }
  }

  function onCasePreview(uuid: string, label: string) {
    showPlusPopover.value = false
    previewCaseUuid.value = uuid
    previewCaseLabel.value = label
    previewOpen.value = true
  }

  function onPreviewAddToChat(uuid: string, label: string) {
    previewOpen.value = false
    showPlusPopover.value = false
    void onSelectCase(uuid, label)
  }

  function onRemoveCase() {
    manualCase.value = null
  }

  function onRemoveDocBadge(id: string) {
    const idx = pendingInputDocs.value.findIndex((d) => d.id === id)
    if (idx < 0) return
    const [removed] = pendingInputDocs.value.splice(idx, 1)
    if (removed.uuid) {
      const uncommittedIdx = uncommittedDocUuids.value.indexOf(removed.uuid)
      if (uncommittedIdx >= 0) uncommittedDocUuids.value.splice(uncommittedIdx, 1)
      DocumentsService.documentsDestroy({ uuid: removed.uuid }).catch((error: unknown) => {
        console.error('Failed to discard uploaded document:', error)
      })
    }
  }

  /**
   * Hydrate the staged ``manualCase`` from a session that already has a case linked.
   *
   * Mirrors the equivalent restore in :class:`SidePanelChat`. ``manualCase`` is
   * component-local state and the only signal driving the locked-state branch of the
   * "+" popover (alongside :const:`caseLocked`, which derives from the session itself).
   * Without this hydration the fullscreen composer mounts with ``manualCase = null`` —
   * even when a case is already attached and locked on the active session — so the
   * popover renders the enabled "Add request" entry instead of the disabled label
   * with the info tooltip when the user expands the side panel into fullscreen or
   * reloads the page on an existing chat.
   *
   * No-op when ``manualCase`` is already set so the ``ensureSession`` race
   * (session created mid-upload while the user's freshly selected case is still
   * in flight) preserves the user's selection.
   *
   * :param session: The active chat session, or ``null``.
   */
  function restoreManualCaseFromSession(session: AIChatSession | null) {
    if (!session?.case_linked_at) return
    if (manualCase.value) return
    const label = session.case_title
    if (!label) return
    manualCase.value = { uuid: session.case_uuid ?? '', label }
  }

  // Reset manual case / pending docs when the active session is genuinely switched or cleared.
  //
  // Important: ``ensureSession()`` may create a session mid-upload (oldUuid=undefined,
  // newUuid=<new>). Treating that transition as a "switch" would clear the pending badges we
  // just populated, so we only act when there was a previous session that actually changed.
  //
  // ``immediate: true`` covers the initial mount path so a fullscreen composer that lands
  // on an existing case-linked session (e.g. expanded from the side panel) hydrates
  // ``manualCase`` and renders the locked-state "+" popover entry. The mid-upload create
  // path also lands here with ``oldUuid=undefined``; ``restoreManualCaseFromSession`` is a
  // no-op when ``manualCase`` is already set, so the user's just-selected case survives.
  watch(
    () => activeChatSession.value?.uuid,
    (newUuid, oldUuid) => {
      if (newUuid === oldUuid) return
      if (!oldUuid) {
        restoreManualCaseFromSession(activeChatSession.value)
        return
      }
      discardUncommittedDocs()
      pendingInputDocs.value = []
      manualCase.value = null
      caseSentWithContext.value = false
      // Drop the thread annotations that belonged to the session we just left.
      clearThreadContextAnnotationsForSession(oldUuid)
      if (newUuid) {
        restoreManualCaseFromSession(activeChatSession.value)
      }
    },
    { immediate: true }
  )

  // Provide session type to child components
  provide('sessionType', sessionType)

  // ---------------
  // Badges displayed above the textarea
  // ---------------

  /** Maximum number of document badges shown before collapsing into a "+N more" popover. */
  const MAX_VISIBLE_DOCS = 2

  interface ChatInputBadge {
    id: string
    label: string
    variant: 'case' | 'pdf' | 'word' | 'other' | 'court-db'
    removable?: boolean
    loading?: boolean
  }

  const docBadges = computed<ChatInputBadge[]>(() =>
    pendingInputDocs.value.map((doc) => ({
      id: doc.id,
      label: doc.filename,
      variant: doc.variant,
      removable: !doc.loading,
      loading: doc.loading
    }))
  )

  const visibleDocBadges = computed(() => docBadges.value.slice(0, MAX_VISIBLE_DOCS))
  const hiddenDocCount = computed(() => Math.max(0, docBadges.value.length - MAX_VISIBLE_DOCS))

  /**
   * Non-document badges shown on the second row of the composer.
   *
   * Order is intentional and stable: the case context badge is **always pinned to
   * position 1** so the conversation's primary context is the leftmost label, regardless
   * of whether court-database was toggled on before the case was attached. Court-database
   * trails it. Mirror this contract in :file:`SidePanelChatInput.vue` if the order changes.
   *
   * Visibility rules:
   *
   * - Case badge: rendered only while pre-send (``!caseLocked``). Once the first message
   *   in case context is sent, the badge is cleared from the input row — the case stays
   *   linked to the session via ``case_uuid``, only the visual label is dropped (mirrors
   *   the document badge clear-on-send contract).
   * - Court-database badge: persists across messages by design. The verdict toggle is a
   *   session-level setting, not a per-message attachment, so it deliberately ignores
   *   ``caseLocked``.
   */
  const nonDocBadges = computed<ChatInputBadge[]>(() => {
    const result: ChatInputBadge[] = []
    if (activeCaseLabel.value && !caseLocked.value) {
      result.push({
        id: '__case__',
        label: activeCaseLabel.value,
        variant: 'case',
        removable: true
      })
    }
    if (courtDatabaseFlagEnabled.value && courtDbEnabled.value) {
      result.push({ id: '__court-db__', label: t('CourtDatabase'), variant: 'court-db' })
    }
    return result
  })

  const hasBadges = computed(() => docBadges.value.length > 0 || nonDocBadges.value.length > 0)

  function onRemoveBadge(id: string) {
    if (id === '__court-db__') {
      toggleCourtDb()
    } else if (id === '__case__') {
      onRemoveCase()
    } else {
      onRemoveDocBadge(id)
    }
  }

  // ---------------
  // Court database toggle
  // ---------------

  const courtDatabaseFlagEnabled = inject<Ref<boolean>>('courtDatabaseFlagEnabled', ref(false))

  // Inject the reactive ref provided by AiChat so it can be read when sending messages
  const courtDbEnabled = inject<Ref<boolean>>('courtDbEnabled', ref(false))
  const setCourtDbEnabled = inject<(value: boolean) => Promise<void>>(
    'setCourtDbEnabled',
    async (value: boolean) => {
      courtDbEnabled.value = value
    }
  )

  const { trackEvent } = useMetrics()

  const toggleCourtDb = () => {
    const newValue = !courtDbEnabled.value
    setCourtDbEnabled(newValue)
    void trackEvent(EventNameEnum.VERDICT_TOGGLE_CHANGED, {
      enabled: newValue,
      session_uuid: activeChatSession.value?.uuid,
      session_type: sessionType
    })
  }

  const dismissVerdictFeatureHint = async () => {
    showVerdictFeatureHint.value = false
    showPlusPopover.value = false

    if (!userStore.user || (userStore.user as any)?.ai_chat_verdict_hint_seen) {
      return
    }

    ;(userStore.user as any).ai_chat_verdict_hint_seen = true

    try {
      await UsersService.usersPartialUpdate({
        uuid: userStore.user.uuid,
        requestBody: {
          ai_chat_verdict_hint_seen: true
        } as any
      })
    } catch (error) {
      console.error('Failed to mark AI chat verdict hint as seen:', error)
    }
  }

  watch(
    courtDatabaseFlagEnabled,
    (isEnabled) => {
      if (!isEnabled) return
      if (!userStore.user || (userStore.user as any)?.ai_chat_verdict_hint_seen) return
      showVerdictFeatureHint.value = true
      showPlusPopover.value = true
      plusPanel.value = 'main'
    },
    { immediate: true }
  )

  const handlePlusPopoverAutoFocus = (event: Event) => {
    if (showVerdictFeatureHint.value) {
      event.preventDefault()
      nextTick(() => {
        courtDatabaseToggleRef.value?.focus()
      })
    }
  }

  const { preventPopoverDismissWhileHint, dismissVerdictHintOnEscape, verdictHintZIndex } =
    useVerdictHintPopoverGuard(showVerdictFeatureHint, dismissVerdictFeatureHint)

  const closePromptPopover = () => {
    isPromptPopoverOpen.value = false
  }

  // Handle prefill from template
  const prefillFromTemplate = (content: string) => {
    userChatMessage.value = content.replace(/^\s+/, '')
    nextTick(() => {
      adjustTextareaHeight()
      textareaRef.value?.focus()
    })
  }

  useComposerPrefill(prefillFromTemplate)

  const handleKeydown = (e: KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      sendMessage()
    }
  }

  // Auto-resize textarea functionality
  const adjustTextareaHeight = () => {
    const textarea = textareaRef.value
    if (!textarea) return

    textarea.style.height = 'auto'

    const lineHeight = 24 // 1.5rem with leading-6
    const minRows = 3
    const minHeight = lineHeight * minRows
    const maxHeight = 270

    const scrollHeight = textarea.scrollHeight
    const newHeight = Math.min(Math.max(scrollHeight, minHeight), maxHeight)

    textarea.style.height = `${newHeight}px`

    isTextareaScrollable.value = scrollHeight > maxHeight
    textarea.style.overflowY = isTextareaScrollable.value ? 'auto' : 'hidden'
  }

  // Watch for changes in userChatMessage to adjust height
  watch(userChatMessage, () => {
    nextTick(() => {
      adjustTextareaHeight()
    })
  })

  // Initialize textarea height + focus on mount
  onMounted(() => {
    nextTick(() => {
      adjustTextareaHeight()
      textareaRef.value?.focus()
    })
  })

  // Re-focus the composer when the user lands on an existing chat session.
  watch(
    () => activeChatSession.value?.uuid,
    (uuid) => {
      if (!uuid) return
      nextTick(() => textareaRef.value?.focus())
    }
  )
</script>

<template>
  <!-- Message Input -->
  <div
    class="w-full min-w-0 max-w-full"
    :class="[
      centerAiAssistantComposer ? 'px-0 pb-0' : 'px-4 pb-4',
      'bg-white',
      !centerAiAssistantComposer && 'mt-auto'
    ]"
    data-cy="message-input-container"
  >
    <div
      class="mx-auto w-full min-w-0"
      :class="centerAiAssistantComposer ? 'max-w-[960px]' : 'max-w-4xl'"
    >
      <!-- Enhancement Error Message -->
      <Transition name="fade">
        <div
          v-if="enhancementError"
          class="mb-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700"
        >
          <div class="flex items-center gap-2">
            <Info class="size-4 shrink-0" />
            <span>{{ enhancementError }}</span>
          </div>
        </div>
      </Transition>

      <div class="relative mt-3">
        <div
          v-if="showVerdictFeatureHint"
          class="pointer-events-auto absolute bottom-full left-4 mb-3"
          :style="{ zIndex: verdictHintZIndex }"
        >
          <AiChatVerdictFeatureHint @dismiss="dismissVerdictFeatureHint" />
        </div>

        <div
          class="flex min-w-0 flex-col gap-3 overflow-hidden rounded-xl border border-gray-200 bg-white p-3 shadow-sm
            transition-colors focus-within:border-purple-600"
          data-cy="message-input-wrapper"
        >
          <!-- Badge row: document badges on the first row, case/court-db on the second -->
          <div
            v-if="hasBadges"
            class="flex flex-col items-start gap-2"
          >
            <div
              v-if="docBadges.length"
              class="flex flex-wrap items-center gap-2 self-stretch"
            >
              <InputBadge
                v-for="badge in visibleDocBadges"
                :key="badge.id"
                :label="badge.label"
                :variant="badge.variant"
                :removable="badge.removable !== false"
                :loading="badge.loading"
                @remove="onRemoveBadge(badge.id)"
              />
              <Popover v-if="hiddenDocCount > 0">
                <PopoverTrigger as-child>
                  <button
                    type="button"
                    class="whitespace-nowrap text-sm font-normal leading-5 text-gray-500 font-inter hover:text-gray-700"
                  >
                    +{{ hiddenDocCount }} {{ t('more') }}
                  </button>
                </PopoverTrigger>
                <PopoverContent
                  side="top"
                  align="start"
                  :side-offset="8"
                  class="min-h-[52px] w-[28rem] max-w-[calc(100vw-2rem)] rounded-xl border-0 bg-white px-3 py-2.5
                    shadow-[0px_4px_8px_0px_rgba(0,0,0,0.08),0px_8px_12px_0px_rgba(0,0,0,0.08)] outline outline-1
                    -outline-offset-1 outline-gray-200"
                >
                  <div class="grid grid-cols-2 gap-2">
                    <InputBadge
                      v-for="badge in docBadges"
                      :key="badge.id"
                      :label="badge.label"
                      :variant="badge.variant"
                      :removable="badge.removable !== false"
                      :loading="badge.loading"
                      @remove="onRemoveBadge(badge.id)"
                    />
                  </div>
                </PopoverContent>
              </Popover>
            </div>

            <div
              v-if="nonDocBadges.length"
              class="flex flex-wrap content-start items-start gap-2"
            >
              <InputBadge
                v-for="badge in nonDocBadges"
                :key="badge.id"
                :label="badge.label"
                :variant="badge.variant"
                :removable="badge.removable !== false"
                :loading="badge.loading"
                @remove="onRemoveBadge(badge.id)"
              />
            </div>
          </div>

          <textarea
            ref="textareaRef"
            v-model="userChatMessage"
            :disabled="!!isActiveSessionGenerating"
            class="block w-full min-w-0 resize-none overflow-y-auto border-0 p-0 text-base font-normal leading-6
              text-gray-800 font-inter placeholder:text-gray-400 focus:outline-none focus:ring-0
              disabled:pointer-events-none disabled:opacity-50 [&::-webkit-scrollbar-thumb]:rounded-full
              [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-track]:bg-gray-100
              [&::-webkit-scrollbar]:w-2"
            :placeholder="messagePlaceholder"
            rows="3"
            data-cy="chat-message-input"
            @keydown="handleKeydown"
            @input="adjustTextareaHeight"
          ></textarea>

          <div
            class="flex w-full min-w-0 max-w-full shrink-0 items-center gap-3"
            data-cy="message-input-actions"
          >
            <!-- Left cluster may shrink; model + actions stay on the row (Figma) -->
            <div
              class="flex min-w-0 flex-1 items-center gap-1 overflow-hidden"
              data-cy="left-button-group"
            >
              <div class="relative">
                <!-- "+" Popover: Add documents · Add request · Court database toggle -->
                <Popover v-model:open="showPlusPopover">
                  <PopoverTrigger as-child>
                    <button
                      type="button"
                      :disabled="!!isActiveSessionGenerating"
                      class="relative flex h-6 w-6 items-center justify-center overflow-hidden disabled:pointer-events-none
                        disabled:opacity-50"
                      :aria-label="t('PlusMenuAria')"
                      data-cy="plus-menu-button"
                    >
                      <Plus
                        class="h-6 w-6"
                        :class="showPlusPopover ? 'text-primary' : 'text-gray-600'"
                        :stroke-width="1.3"
                      />
                    </button>
                  </PopoverTrigger>
                  <!--
                    ``overflow-hidden`` clips inner content to the popover's rounded
                    border-radius. Without it, the case-search sub-panel's rectangular
                    bottom-fade gradient (and any other rectangular white surfaces inside)
                    bleed past the rounded corners and read as a flat rectangular frame
                    behind the popover.
                  -->
                  <PopoverContent
                    side="top"
                    align="start"
                    :side-offset="12"
                    class="min-h-[52px] rounded-xl border-0 bg-white p-0
                      shadow-[0px_4px_8px_0px_rgba(0,0,0,0.08),0px_8px_12px_0px_rgba(0,0,0,0.08)] outline outline-1
                      -outline-offset-1 outline-gray-200"
                    :class="plusPanel === 'cases' ? 'w-72' : 'w-72 py-2.5'"
                    @open-auto-focus="handlePlusPopoverAutoFocus"
                    @pointer-down-outside="preventPopoverDismissWhileHint"
                    @focus-outside="preventPopoverDismissWhileHint"
                    @escape-key-down="dismissVerdictHintOnEscape"
                  >
                    <!-- Case search sub-panel -->
                    <template v-if="plusPanel === 'cases'">
                      <CaseSearchPanel
                        @select="onSelectCase"
                        @preview="onCasePreview"
                        @back="plusPanel = 'main'"
                      />
                    </template>

                    <!-- Default menu -->
                    <template v-else>
                      <!-- Add documents -->
                      <button
                        type="button"
                        class="flex w-full items-center gap-2.5 border-b border-gray-50 p-4 text-left hover:bg-gray-50"
                        data-cy="add-documents-button"
                        @click="triggerFilePicker"
                      >
                        <Paperclip
                          class="h-5 w-5 shrink-0 text-gray-800"
                          :stroke-width="1.25"
                        />
                        <span class="text-sm font-normal leading-5 text-gray-800 font-inter">
                          {{ t('AddDocuments') }}
                        </span>
                      </button>

                      <!-- Add request: normal / locked states -->
                      <TooltipProvider
                        v-if="caseLocked && manualCase"
                        :delay-duration="300"
                      >
                        <Tooltip>
                          <TooltipTrigger as-child>
                            <button
                              type="button"
                              disabled
                              class="flex w-full items-center gap-2.5 border-b border-gray-50 p-4 text-left"
                            >
                              <FolderInput
                                class="h-5 w-5 shrink-0 text-gray-400"
                                :stroke-width="1.25"
                              />
                              <span
                                class="line-clamp-1 flex-1 text-sm font-normal leading-5 text-gray-400 font-inter"
                              >
                                {{ manualCase.label }}
                              </span>
                              <InfoIcon
                                class="h-5 w-5 shrink-0 text-gray-800"
                                :stroke-width="1.25"
                              />
                            </button>
                          </TooltipTrigger>
                          <TooltipContent
                            side="top"
                            class="max-w-[16rem]"
                          >
                            {{ t('caseAlreadyAdded') }}
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                      <!--
                        Pre-send "Add request" affordance.

                        Stays visible whenever the case is not yet locked (post-send) so the
                        user can switch contexts without first removing the current badge.
                        ``onSelectCase`` replaces ``manualCase`` in place, and the subsequent
                        ``sendMessage`` / ``commitCaseToExistingSession`` pair PATCHes the new
                        ``case_uuid`` onto an existing session when it differs from the staged
                        case, so a pre-send switch flows through end-to-end.
                      -->
                      <button
                        v-else
                        type="button"
                        class="flex w-full items-center gap-2.5 border-b border-gray-50 p-4 text-left hover:bg-gray-50"
                        data-cy="add-request-button"
                        @click="plusPanel = 'cases'"
                      >
                        <FolderInput
                          class="h-5 w-5 shrink-0 text-gray-800"
                          :stroke-width="1.25"
                        />
                        <span class="text-sm font-normal leading-5 text-gray-800 font-inter">
                          {{ t('AddRequest') }}
                        </span>
                      </button>

                      <!-- Court database toggle -->
                      <div
                        v-if="courtDatabaseFlagEnabled"
                        ref="courtDatabaseToggleRef"
                        class="flex items-center gap-2.5 p-4 outline-none focus:outline-none focus-visible:outline-none"
                        :class="
                          showVerdictFeatureHint ? 'rounded-md border-[3px] border-purple-600' : ''
                        "
                        data-cy="court-database-toggle"
                      >
                        <Section
                          class="h-5 w-5 shrink-0 text-gray-800"
                          :stroke-width="1.25"
                        />
                        <span class="flex-1 text-sm font-normal leading-5 text-gray-800 font-inter">
                          {{ t('CourtDatabase') }}
                        </span>
                        <Switch
                          :checked="courtDbEnabled"
                          class="h-5 w-9 data-[state=checked]:bg-primary data-[state=unchecked]:bg-gray-200"
                          thumb-class="h-4 w-4 data-[state=checked]:translate-x-4 data-[state=unchecked]:translate-x-0"
                          @update:checked="toggleCourtDb"
                        />
                      </div>
                    </template>
                  </PopoverContent>
                </Popover>
              </div>

              <button
                type="button"
                :disabled="!!isActiveSessionGenerating"
                class="inline-flex min-w-0 max-w-full items-center justify-start gap-1 truncate rounded-full px-3 py-1
                  text-sm font-normal leading-5 text-gray-600 transition-colors font-inter hover:bg-gray-50
                  focus:outline-none disabled:pointer-events-none disabled:opacity-50"
                data-cy="inline-prompt-templates-button"
                @click="openPromptTemplates"
              >
                <div class="relative h-6 w-6 shrink-0 overflow-hidden">
                  <ListRestart
                    class="h-6 w-6 text-gray-600"
                    :stroke-width="1.3"
                  />
                </div>
                <span class="min-w-0 truncate">{{ t('Prompts') }}</span>
              </button>
            </div>

            <ModelSelector
              :model-id="selectedModelId"
              :models="unifiedChatStore.availableModels"
              :disabled="isActiveSessionGenerating ?? false"
              :chat-context="toChatContext(sessionType, false)"
              data-cy="model-display"
              @update:model-id="selectedModelId = $event"
            />

            <div
              class="flex shrink-0 items-center gap-3"
              data-cy="right-button-group"
            >
              <!-- Enhance Prompt Button -->
              <div class="relative">
                <button
                  type="button"
                  :disabled="
                    isEnhancingPrompt || isActiveSessionGenerating || !userChatMessage.trim()
                  "
                  class="inline-flex shrink-0 items-center justify-center text-sm font-medium focus:outline-none
                    disabled:pointer-events-none disabled:opacity-50"
                  :class="enhancePromptVariantClass"
                  data-cy="enhance-prompt-button"
                  @click="enhancePrompt"
                  @mouseenter="showEnhanceTooltip = true"
                  @mouseleave="showEnhanceTooltip = false"
                >
                  <svg
                    v-if="isEnhancingPrompt"
                    class="h-5 w-5 animate-spin"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden="true"
                  >
                    <circle
                      cx="8"
                      cy="8"
                      r="6"
                      stroke="#e5e7eb"
                      stroke-width="2"
                    />
                    <path
                      d="M14 8a6 6 0 0 0-6-6"
                      :stroke="`url(#${spinnerGradientId})`"
                      stroke-width="2"
                      stroke-linecap="round"
                    />
                    <defs>
                      <linearGradient
                        :id="spinnerGradientId"
                        x1="8"
                        y1="2"
                        x2="14"
                        y2="8"
                        gradientUnits="userSpaceOnUse"
                      >
                        <stop stop-color="#EF469B" />
                        <stop
                          offset="1"
                          stop-color="#9333EA"
                        />
                      </linearGradient>
                    </defs>
                  </svg>
                  <span v-else>
                    <svg
                      width="16"
                      height="16"
                      class="h-4 w-4 shrink-0"
                      viewBox="0 0 16 16"
                      fill="none"
                      aria-hidden="true"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M10.0002 3.33333L12.6668 6M3.3335 4V6.66667M12.6668 9.33333V12M6.66683 1.33333V2.66667M4.66683 5.33333H2.00016M14.0002 10.6667H11.3335M7.3335 2H6.00016M14.1161 4.54126C14.4686 4.18888 14.6666 3.71091 14.6667 3.2125C14.6668 2.71409 14.4688 2.23607 14.1165 1.8836C13.7641 1.53112 13.2861 1.33307 12.7877 1.33301C12.2893 1.33295 11.8113 1.53088 11.4588 1.88326L2.56145 10.7826C2.40667 10.9369 2.29219 11.127 2.22812 11.3359L1.34745 14.2373C1.33022 14.2949 1.32892 14.3562 1.34369 14.4145C1.35845 14.4728 1.38873 14.5261 1.43132 14.5686C1.4739 14.6111 1.5272 14.6413 1.58556 14.656C1.64392 14.6707 1.70516 14.6693 1.76279 14.6519L4.66479 13.7719C4.87357 13.7084 5.06357 13.5947 5.21812 13.4406L14.1161 4.54126Z"
                        :stroke="`url(#${sparkleGradientId})`"
                        stroke-width="1.2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                      <defs>
                        <linearGradient
                          :id="sparkleGradientId"
                          x1="1.3335"
                          y1="7.99952"
                          x2="14.6667"
                          y2="7.99952"
                          gradientUnits="userSpaceOnUse"
                        >
                          <stop stop-color="#EF469A" />
                          <stop
                            offset="1"
                            stop-color="#9333EA"
                          />
                        </linearGradient>
                      </defs>
                    </svg>
                  </span>
                  <span class="sr-only">
                    {{ isEnhancingPrompt ? t('Enhancing') : t('EnhancePrompt') }}
                  </span>
                </button>
                <!-- Custom Tooltip -->
                <Transition name="tooltip">
                  <div
                    v-if="showEnhanceTooltip && !isEnhancingPrompt"
                    class="absolute bottom-full left-1/2 mb-2 -translate-x-1/2 transform whitespace-nowrap rounded bg-gray-900
                      px-2 py-1 text-xs text-white shadow-lg"
                  >
                    {{ t('EnhancePrompt') }}
                    <div
                      class="absolute left-1/2 top-full -translate-x-1/2 transform border-4 border-transparent border-t-gray-900"
                    ></div>
                  </div>
                </Transition>
              </div>

              <!-- Send Button -->
              <button
                type="button"
                :disabled="
                  !!isActiveSessionGenerating || !userChatMessage.trim() || uploadsInProgress
                "
                class="inline-flex shrink-0 items-center justify-center text-sm font-medium focus:outline-none
                  disabled:pointer-events-none disabled:opacity-50"
                :class="sendButtonVariantClass"
                data-cy="send-message-button"
                @click="sendMessage"
              >
                <span
                  v-if="isActiveSessionGenerating"
                  class="animate-spin"
                >
                  <Loader2 class="size-4 shrink-0 text-primary-foreground" />
                </span>
                <span
                  v-else
                  class="relative flex h-5 w-5 items-center justify-center overflow-hidden"
                >
                  <div class="relative h-5 w-5 overflow-hidden">
                    <ArrowUp
                      class="h-5 w-5 text-primary-foreground"
                      :stroke-width="1.5"
                    />
                  </div>
                </span>
                <span class="sr-only">
                  {{ isActiveSessionGenerating ? t('Generating') : t('SendMessage') }}
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  <!-- End Message Input -->

  <!-- Prompt Templates Dialog (opened from + menu or inline Prompts) -->
  <Dialog v-model:open="isPromptPopoverOpen">
    <DialogContent class="max-w-5xl overflow-hidden rounded-lg p-0">
      <PromptTemplatesDialog
        @close="closePromptPopover"
        @prefill="prefillFromTemplate"
      />
    </DialogContent>
  </Dialog>

  <!-- Hidden file input used by the "Add documents" menu item -->
  <input
    ref="fileInputRef"
    type="file"
    multiple
    :accept="FILE_INPUT_ACCEPT"
    class="hidden"
    @change="onFileInput"
  />

  <!-- Case preview dialog triggered from the case search list -->
  <CasePreviewDialog
    :open="previewOpen"
    :case-uuid="previewCaseUuid"
    :case-label="previewCaseLabel"
    @update:open="previewOpen = $event"
    @add-to-chat="onPreviewAddToChat"
  />
</template>

<style scoped>
  .fade-enter-active,
  .fade-leave-active {
    transition:
      opacity 0.4s ease,
      transform 0.3s ease;
  }

  .fade-enter-from {
    opacity: 0;
    transform: translateY(-5px);
  }

  .fade-leave-to {
    opacity: 0;
    transform: translateY(5px);
  }

  .fade-enter-to,
  .fade-leave-from {
    opacity: 1;
    transform: translateY(0);
  }

  .animate-spin {
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
</style>
